import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

/**
 * Shared layout for all legal pages (Terms, Refund, Return, Shipping, Privacy).
 * `sections` is an array of { heading?, body?, list?, emphasis? }.
 */
// Per-legal-page keyword sets — help these pages rank for policy-related
// queries (users search things like "cake shop return policy mumbai").
const LEGAL_KEYWORDS = {
  "terms-page": "terms and conditions, terms of service, cocoamaya patisserie terms, mumbai bakery terms",
  "privacy-page": "privacy policy, data protection, cocoamaya privacy, mumbai bakery privacy",
  "refund-page": "refund policy, cake refund mumbai, cocoamaya refund, order refund",
  "return-page": "return policy, cake return mumbai, cocoamaya return, order return",
  "shipping-page": "shipping policy, cake delivery mumbai, cocoamaya shipping, order delivery",
};

export const LegalPage = ({ eyebrow = "Legal", title, lastUpdated = "April 2026", sections, testid }) => {
  const seoTitle = `${title} | Cocoamaya Patisserie`;
  const seoDesc = `${title} for Cocoamaya Patisserie — the policies governing use of our website and services.`;
  const path = "/" + (testid || "").replace(/-page$/, "");
  const seoKeywords = LEGAL_KEYWORDS[testid] || "cocoamaya patisserie mumbai";
  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 md:py-20" data-testid={testid}>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        url={`https://cocoamayapatisserie.com${path}`}
      />
      <Link
        to="/"
        className="eyebrow inline-flex items-center gap-2 mb-8 hover:text-[var(--accent)] transition-colors"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Back home
      </Link>

      <header className="mb-12 md:mb-16">
        <p className="eyebrow mb-3" style={{ color: "var(--accent)" }}>{eyebrow}</p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight italic">{title}</h1>
        <p className="mt-5 text-sm" style={{ color: "var(--ink-soft)" }}>
          Last updated: {lastUpdated}
        </p>
      </header>

      <article className="space-y-8 md:space-y-10 text-base leading-relaxed" style={{ color: "var(--ink)" }}>
        {sections.map((s, i) => (
          <section key={i}>
            {s.heading && (
              <h2
                className="font-display text-2xl md:text-3xl italic mb-4"
                style={{ color: "var(--ink)" }}
              >
                {s.heading}
              </h2>
            )}
            {s.body && (
              <p
                className={s.emphasis ? "font-medium" : ""}
                style={{ color: s.emphasis ? "var(--ink)" : "var(--ink-soft)" }}
              >
                {s.body}
              </p>
            )}
            {s.list && (
              <ul className="mt-4 space-y-3 pl-1">
                {s.list.map((item, j) => (
                  <li key={j} className="flex gap-3" style={{ color: "var(--ink-soft)" }}>
                    <span className="font-display text-xl leading-none mt-0.5" style={{ color: "var(--accent)" }}>·</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </article>

      <div className="mt-16 pt-10 border-t" style={{ borderColor: "var(--line)" }}>
        <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
          Questions? Reach us at{" "}
          <a href="mailto:hello@cocoamayapatisserie.com" className="underline" style={{ color: "var(--accent)" }}>
            hello@cocoamayapatisserie.com
          </a>
          {" "}or call{" "}
          <a href="tel:+919819300124" className="underline" style={{ color: "var(--accent)" }}>
            +91 98193 00124
          </a>.
        </p>
      </div>
    </div>
  );
};
